// src/pages/obs/SubastaOBSPage.tsx
// ─── Mux v1 — HLS nativo + WebSocket estado ──────────────────────────────────────
// CAMBIOS RESPECTO A VERSIÓN MUX:
//  - muxPlaybackUrl / muxStatus → livekitIngressUrl / livekitStatus
//  - refreshMuxStatus() → refreshMuxStatus()
//  - WebSocket /topic/stream-status: campo "muxPlaybackUrl" → "livekitIngressUrl"
//  - Todo lo demás (WebSocket pujas, lotes, design) sin cambios

import React, { useEffect, useRef, useState } from "react";
import { Client, IMessage } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import {
  AuctionLot,
  LotBidAdmin,
  formatCOP,
  formatDateEs,
  getSubastaConfig,
  refreshMuxStatus,
} from "../../api/auctionLotApi";
import { BASE_URL } from "../../api/client";

// ─── HLS.JS — player para CEF/OBS ────────────────────────────────────────────
// hls.js via jsdelivr (sin CORS, sin problemas en CEF/OBS).
// Funciona con el HLS Egress de LiveKit exactamente igual que con Mux.
const HLS_CDN = "https://cdn.jsdelivr.net/npm/hls.js@1.5.7/dist/hls.min.js";
let hlsReadyPromise: Promise<any> | null = null;

function preloadHlsJs(): Promise<any> {
  if (hlsReadyPromise) return hlsReadyPromise;
  if ((window as any).Hls) {
    hlsReadyPromise = Promise.resolve((window as any).Hls);
    return hlsReadyPromise;
  }
  hlsReadyPromise = new Promise((resolve) => {
    const ex = document.querySelector(`script[src="${HLS_CDN}"]`);
    if (ex) {
      if ((window as any).Hls) {
        resolve((window as any).Hls);
        return;
      }
      ex.addEventListener("load", () => resolve((window as any).Hls));
      return;
    }
    const s = document.createElement("script");
    s.src = HLS_CDN;
    s.async = true;
    s.onload = () => resolve((window as any).Hls);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return hlsReadyPromise;
}
preloadHlsJs();

// ─── CACHE sessionStorage ─────────────────────────────────────────────────────
const CACHE_KEY = "fg_obs_mux_v1";
function saveCache(muxPlaybackUrl: string, status: string) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ muxPlaybackUrl, status, ts: Date.now() }),
    );
  } catch {}
}
function loadCache(): { muxPlaybackUrl: string; status: string } | null {
  try {
    const r = sessionStorage.getItem(CACHE_KEY);
    if (!r) return null;
    const p = JSON.parse(r);
    if (Date.now() - p.ts > 60 * 60 * 1000) return null; // 1 hora
    return {
      muxPlaybackUrl: p.muxPlaybackUrl ?? p.ingressUrl,
      status: p.status,
    };
  } catch {
    return null;
  }
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function parseVideoUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (raw.startsWith("http")) return raw;
  const m = raw.match(/url=([^,}]+)/);
  return m ? m[1].trim() : raw;
}

async function getLoteActivoPublico(): Promise<AuctionLot | null> {
  try {
    const r = await fetch(`${BASE_URL}/admin/lots/active`, {
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return null;
    const j = await r.json();
    const lot = j?.content ?? null;
    if (lot?.videoUrl) lot.videoUrl = parseVideoUrl(lot.videoUrl);
    return lot;
  } catch {
    return null;
  }
}

async function getBidsPublico(lotId: string): Promise<LotBidAdmin[]> {
  try {
    const r = await fetch(`${BASE_URL}/lot-bids/${lotId}`, {
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return [];
    const j = await r.json();
    const list = j?.content ?? j;
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

// MuxHlsPlayer original eliminado — reemplazado por MuxHlsPlayerWithRef
// que expone el ref del <video> para detectar el evento canplay
// y evitar el fondo negro mientras HLS bufferiza.

// ─── MUX HLS PLAYER CON OVERLAY ANTI-NEGRO ──────────────────────────────────
// Envuelve MuxHlsPlayer con un fondo oscuro (#0a1a2a) que se muestra
// mientras el HLS bufferiza — evita el negro puro del elemento <video>.
// El overlay desaparece en cuanto el video empieza a decodificar frames.
function MuxHlsPlayerWithOverlay({ playbackUrl }: { playbackUrl: string }) {
  const [videoVisible, setVideoVisible] = React.useState(false);

  // Detectar cuando el video realmente tiene frames disponibles
  const handleVideoRef = React.useCallback((video: HTMLVideoElement | null) => {
    if (!video) return;
    // canplay: el codec tiene suficiente data para empezar — sin negro
    const onCanPlay = () => setVideoVisible(true);
    video.addEventListener("canplay", onCanPlay);
    // Si ya está listo (carga rápida) lo mostramos igual
    if (video.readyState >= 3) setVideoVisible(true);
  }, []);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {/* Fondo oscuro SIEMPRE presente debajo del video */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "#0a1a2a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        {!videoVisible && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>📡</div>
            <div style={{ color: "#0cbb1d", fontSize: 16, fontWeight: 700 }}>
              Conectando stream...
            </div>
          </div>
        )}
      </div>
      {/* Video encima — visible solo cuando tiene frames (canplay) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: videoVisible ? 1 : 0,
          transition: "opacity 0.3s ease",
          zIndex: 2,
        }}
      >
        <MuxHlsPlayerWithRef
          key={playbackUrl}
          playbackUrl={playbackUrl}
          videoRef={handleVideoRef}
        />
      </div>
    </div>
  );
}

// MuxHlsPlayer con ref expuesto para detectar canplay
function MuxHlsPlayerWithRef({
  playbackUrl,
  videoRef: externalRef,
}: {
  playbackUrl: string;
  videoRef: (v: HTMLVideoElement | null) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<any>(null);
  const watchdogRef = useRef<any>(null);

  // Conectar el ref externo
  useEffect(() => {
    externalRef(videoRef.current);
    return () => externalRef(null);
  }, [externalRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl) return;
    let cancelled = false;

    const startWatchdog = (hls: any) => {
      if (watchdogRef.current) clearInterval(watchdogRef.current);
      watchdogRef.current = setInterval(() => {
        if (!video || !hls) return;
        try {
          const livePos = hls.liveSyncPosition;
          if (livePos != null && livePos > 0 && !video.paused) {
            if (livePos - video.currentTime > 5) video.currentTime = livePos;
          }
          if (video.paused && !video.ended) video.play().catch(() => {});
        } catch {}
      }, 500);
    };

    const initPlayer = (Hls: any) => {
      if (cancelled || !Hls) return;
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch {}
      }
      if (watchdogRef.current) clearInterval(watchdogRef.current);

      if (!Hls.isSupported()) {
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = playbackUrl;
          video.play().catch(() => {});
        }
        return;
      }

      const hls = new Hls({
        lowLatencyMode: true,
        startPosition: -1,
        liveSyncDurationCount: 1,
        liveMaxLatencyDurationCount: 4,
        backBufferLength: 4,
        maxBufferLength: 6,
        maxMaxBufferLength: 6,
        highBufferWatchdogPeriod: 1,
        manifestLoadingMaxRetry: 20,
        manifestLoadingRetryDelay: 250,
        levelLoadingMaxRetry: 20,
        levelLoadingRetryDelay: 250,
        fragLoadingMaxRetry: 20,
        fragLoadingRetryDelay: 250,
      });
      hls.loadSource(playbackUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (cancelled) return;
        if (hls.liveSyncPosition != null && hls.liveSyncPosition > 0) {
          video.currentTime = hls.liveSyncPosition;
        }
        video.play().catch(() => {});
        startWatchdog(hls);
      });
      hls.on(Hls.Events.ERROR, (_: any, data: any) => {
        if (!data.fatal || cancelled) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          setTimeout(() => {
            if (!cancelled) {
              try {
                hls.startLoad(-1);
              } catch {
                initPlayer(Hls);
              }
            }
          }, 1000);
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          setTimeout(() => {
            if (!cancelled) initPlayer(Hls);
          }, 2000);
        }
      });
      hlsRef.current = hls;
    };

    preloadHlsJs().then(initPlayer);

    return () => {
      cancelled = true;
      if (watchdogRef.current) clearInterval(watchdogRef.current);
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch {}
        hlsRef.current = null;
      }
    };
  }, [playbackUrl]);

  return (
    <video
      ref={videoRef}
      style={styles.videoEl}
      autoPlay
      playsInline
      muted={false}
      controls={false}
    />
  );
}

// ─── VIDEO PLAYER PRINCIPAL ───────────────────────────────────────────────────
function OBSVideoPlayer({
  lot,
  livekitHlsUrl,
  livekitStatus,
}: {
  lot: AuctionLot | null;
  livekitHlsUrl: string | null; // muxPlaybackUrl
  livekitStatus: string | null; // muxStatus
}) {
  const isLive = !!livekitHlsUrl && livekitStatus === "active";

  if (isLive) {
    return (
      <div style={styles.videoWrapper}>
        <div style={{ ...styles.liveBadge, backgroundColor: "#dc3545" }}>
          ● EN VIVO
        </div>
        {/* MuxHlsPlayerWithOverlay: muestra fondo oscuro mientras HLS bufferiza */}
        <MuxHlsPlayerWithOverlay playbackUrl={livekitHlsUrl} />
      </div>
    );
  }

  if (lot?.videoUrl) {
    return (
      <div style={styles.videoWrapper}>
        {livekitHlsUrl && (
          <div style={{ ...styles.liveBadge, backgroundColor: "#f98115" }}>
            ⏳ OBS no conectado
          </div>
        )}
        <video
          src={lot.videoUrl}
          style={styles.videoEl}
          autoPlay
          muted
          loop
          playsInline
        />
      </div>
    );
  }

  return (
    <div style={styles.videoPlaceholder}>
      <div style={{ fontSize: 64, marginBottom: 12 }}>🐄</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#0cbb1d" }}>
        Feriagana — Subastas en Vivo
      </div>
      <div
        style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", marginTop: 8 }}
      >
        Esperando transmisión...
      </div>
    </div>
  );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
export default function SubastaOBSPage() {
  // Aislar estilos del panel admin — Bootstrap no debe contaminar esta página
  useEffect(() => {
    const prev = document.body.getAttribute("style") || "";
    document.body.style.cssText =
      "margin:0;padding:0;background:#000;overflow:hidden;";
    return () => {
      document.body.setAttribute("style", prev);
    };
  }, []);

  const cached = loadCache();

  const [activeLot, setActiveLot] = useState<AuctionLot | null>(null);
  const [bids, setBids] = useState<LotBidAdmin[]>([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [lotClosed, setLotClosed] = useState(false);
  const [ganador, setGanador] = useState<{
    paleta: number;
    monto: number | null;
  } | null>(null);

  // Mux: muxPlaybackUrl = URL .m3u8
  const [muxPlaybackUrl, setMuxPlaybackUrl] = useState<string | null>(
    cached?.muxPlaybackUrl ?? null,
  );
  const [muxStatus, setMuxStatus] = useState<string | null>(
    cached?.status ?? null,
  );

  const activeLotIdRef = useRef<string | null>(null);
  const setBidsRef = useRef(setBids);
  useEffect(() => {
    setBidsRef.current = setBids;
  });

  // ── Carga inicial + poll de respaldo ─────────────────────────────────────
  useEffect(() => {
    const cargarEstadoInicial = async () => {
      const [cfgResult, statusResult] = await Promise.allSettled([
        getSubastaConfig(),
        refreshMuxStatus(),
      ]);

      let url: string | null = null;
      let st: string | null = null;

      if (cfgResult.status === "fulfilled") {
        const cfg = cfgResult.value as any;
        url = cfg.muxPlaybackUrl ?? null;
      }
      if (statusResult.status === "fulfilled") {
        const s = statusResult.value;
        if (s.status !== "unknown") st = s.status;
        if (!url && s.muxPlaybackUrl) url = s.muxPlaybackUrl;
      }

      if (url) setMuxPlaybackUrl((prev) => (prev !== url ? url : prev));
      if (st) setMuxStatus((prev) => (prev !== st ? st : prev));
      if (url && st) saveCache(url, st);
    };

    cargarEstadoInicial();

    // Poll continuo cada 5s — NUNCA se cancela (clearInterval eliminado).
    // Si cancelamos al detectar "active", no detectamos cuando el stream cae
    // y vuelve a subir en la misma sesión (OBS reconecta después de un corte).
    // El poll es liviano: solo consulta BD local, no llama a la API de Mux.
    const pollId = setInterval(async () => {
      try {
        const s = await refreshMuxStatus();
        if (s.status && s.status !== "unknown") {
          const newUrl = s.muxPlaybackUrl || null;
          if (newUrl)
            setMuxPlaybackUrl((prev) => (prev !== newUrl ? newUrl : prev));
          setMuxStatus((prev) => {
            if (prev !== s.status) {
              if (newUrl && s.status) saveCache(newUrl, s.status);
              return s.status;
            }
            return prev;
          });
          // FIX: NO hacer clearInterval — el stream puede caer y volver
        }
      } catch {}
    }, 5000);

    return () => clearInterval(pollId);
  }, []);

  // ── Lote activo inicial ───────────────────────────────────────────────────
  useEffect(() => {
    getLoteActivoPublico().then((lot) => {
      if (lot) {
        setActiveLot(lot);
        activeLotIdRef.current = lot.id;
        setLotClosed(lot.status === "CERRADO");
        if (lot.status === "CERRADO" && lot.paletaGanadora) {
          setGanador({ paleta: lot.paletaGanadora, monto: lot.montoGanador });
        }
        getBidsPublico(lot.id).then((list) => setBids(sortBids(list)));
      }
    });
  }, []);

  // ── WebSocket — lotes + stream-status ────────────────────────────────────
  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => new SockJS(`${BASE_URL}/ws`) as any,
      reconnectDelay: 3000,
      onConnect: () => {
        setWsConnected(true);

        // ── /topic/stream-status — estado Mux en tiempo real ──────────────
        // El webhook de Mux → backend → WS → aquí.
        // Campo "muxPlaybackUrl" en el mensaje WS.
        client.subscribe("/topic/stream-status", (msg: IMessage) => {
          try {
            const evt = JSON.parse(msg.body || "{}");
            if (evt.type !== "STREAM_STATUS") return;

            const newStatus: string = evt.status ?? "idle";
            const newUrl: string | null = evt.muxPlaybackUrl || null;

            setMuxStatus((prev) => (prev !== newStatus ? newStatus : prev));
            if (newUrl) {
              setMuxPlaybackUrl((prev) => (prev !== newUrl ? newUrl : prev));
              saveCache(newUrl, newStatus);
            }
          } catch {}
        });

        // ── /topic/lot-bids/:lotId — pujas en tiempo real ─────────────────
        const addBidDeduped = (
          bidId: string,
          palletNumber: number,
          amount: number,
          createdAt: string,
          userId: string | null,
          userName: string,
        ) => {
          setBidsRef.current((prev) => {
            const isDup = prev.some(
              (b) =>
                (b.bidId && b.bidId !== "" && b.bidId === bidId) ||
                (b.amount === amount && b.createdAt === createdAt),
            );
            if (isDup) return prev;
            const nb: LotBidAdmin = {
              bidId,
              palletNumber,
              amount,
              createdAt,
              userId,
              userName,
              userEmail: null,
              esUltimaOferta: false,
            };
            const updated = [
              nb,
              ...prev.map((b) => ({ ...b, esUltimaOferta: false })),
            ];
            if (updated.length > 0) updated[0].esUltimaOferta = true;
            return updated.slice(0, 20);
          });
        };

        let subBid: any = null;
        const subscribeToLot = (lotId: string) => {
          try {
            subBid?.unsubscribe();
          } catch {}
          subBid = client.subscribe(
            `/topic/lot-bids/${lotId}`,
            (msg: IMessage) => {
              try {
                const evt = JSON.parse(msg.body || "{}");
                if (evt?.amount != null && evt?.palletNumber != null) {
                  addBidDeduped(
                    evt.bidId ?? String(evt.amount) + (evt.createdAt ?? ""),
                    Number(evt.palletNumber),
                    Number(evt.amount),
                    evt.createdAt ?? new Date().toISOString(),
                    evt.userId ?? null,
                    evt.userName ?? "—",
                  );
                }
              } catch {}
            },
          );
        };

        if (activeLotIdRef.current) subscribeToLot(activeLotIdRef.current);

        client.subscribe("/topic/lots", (msg: IMessage) => {
          try {
            const evt = JSON.parse(msg.body || "{}") as any;
            const evtType: string = evt?.type ?? "";

            if (evtType === "LOT_BID") {
              const amount = Number(evt.currentBid ?? evt.amount ?? 0);
              if (amount > 0) {
                addBidDeduped(
                  evt.bidId ?? String(amount) + (evt.createdAt ?? ""),
                  Number(evt.palletNumber ?? 0),
                  amount,
                  evt.createdAt ?? new Date().toISOString(),
                  evt.userId ?? null,
                  "—",
                );
              }
              return;
            }

            if (evtType === "LOT_ACTIVATED") {
              const newLot: AuctionLot = {
                id: evt.lotId,
                nroLote: evt.nroLote,
                orden: evt.orden,
                videoUrl: parseVideoUrl(evt.videoUrl),
                cantAnimales: evt.cantAnimales,
                tipoGanado: evt.tipoGanado,
                pesoProm: evt.pesoProm,
                pesoTotal: evt.pesoTotal,
                ubicacion: evt.ubicacion,
                precioMinimo: evt.precioMinimo,
                valorKg: evt.valorKg,
                valorPorAnimal: evt.valorPorAnimal,
                observaciones: null,
                status: evt.status,
                paletaGanadora: null,
                usuarioGanadorId: null,
                montoGanador: null,
                publishedId: null,
                createdAt: null,
                updatedAt: null,
              };
              setActiveLot(newLot);
              activeLotIdRef.current = evt.lotId;
              setLotClosed(false);
              setGanador(null);
              setBidsRef.current([]);
              subscribeToLot(evt.lotId);
              getBidsPublico(evt.lotId).then((list) =>
                setBidsRef.current(sortBids(list)),
              );
            }

            if (evtType === "LOT_CLOSED") {
              setActiveLot((prev) =>
                prev
                  ? {
                      ...prev,
                      status: "CERRADO",
                      paletaGanadora: evt.paletaGanadora,
                      montoGanador: evt.montoGanador,
                    }
                  : prev,
              );
              setLotClosed(true);
              if (evt.paletaGanadora) {
                setGanador({
                  paleta: evt.paletaGanadora,
                  monto: evt.montoGanador,
                });
              }
            }
          } catch {}
        });
      },
      onStompError: () => setWsConnected(false),
      onWebSocketClose: () => setWsConnected(false),
    });

    client.activate();
    return () => {
      try {
        client.deactivate();
      } catch {}
    };
  }, []);

  function sortBids(list: LotBidAdmin[]): LotBidAdmin[] {
    return [...list]
      .sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      })
      .map((b, i) => ({ ...b, esUltimaOferta: i === 0 }));
  }

  const topBid = bids[0] ?? null;

  return (
    <>
      {/* Reset global — evita que Bootstrap del panel admin se filtre */}
      <style>{`
        body { margin: 0 !important; padding: 0 !important;
               background: #000 !important; overflow: hidden !important; }
        #root { background: #000 !important; }
      `}</style>

      <div style={styles.root}>
        {/* Badge WS */}
        <div style={styles.wsBadge}>
          <span
            style={{
              color: wsConnected ? "#0cbb1d" : "#dc3545",
              fontWeight: 800,
            }}
          >
            {wsConnected ? "● EN VIVO" : "○ Reconectando..."}
          </span>
        </div>

        <div style={styles.grid}>
          <div style={styles.colVideo}>
            <OBSVideoPlayer
              lot={activeLot}
              livekitHlsUrl={muxPlaybackUrl}
              livekitStatus={muxStatus}
            />
          </div>

          <div style={styles.colBids}>
            {lotClosed && ganador && (
              <div style={styles.winnerCard}>
                <div style={{ fontSize: 32, marginBottom: 6 }}>🏆</div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#fff",
                    marginBottom: 4,
                  }}
                >
                  ¡LOTE VENDIDO!
                </div>
                <div
                  style={{ fontSize: 28, fontWeight: 900, color: "#0cbb1d" }}
                >
                  Paleta #{String(ganador.paleta).padStart(3, "0")}
                </div>
                {ganador.monto && (
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: "#fff",
                      marginTop: 4,
                    }}
                  >
                    {formatCOP(ganador.monto)}
                  </div>
                )}
              </div>
            )}

            {topBid && !lotClosed && (
              <div style={styles.topBidCard}>
                <div style={styles.topBidLabel}>💰 Oferta actual</div>
                <div style={styles.topBidPaleta}>
                  Paleta #{String(topBid.palletNumber).padStart(3, "0")}
                </div>
                <div style={styles.topBidAmount}>
                  {formatCOP(topBid.amount)}
                </div>
                <div style={styles.topBidDate}>
                  {formatDateEs(topBid.createdAt)}
                </div>
              </div>
            )}

            <div style={styles.bidsTitle}>Ofertas realizadas</div>
            <div style={styles.bidsList}>
              {bids.length === 0 ? (
                <div style={styles.noBids}>Aún no hay ofertas</div>
              ) : (
                bids.slice(0, 8).map((bid, idx) => (
                  <div
                    key={`${bid.bidId}-${idx}`}
                    style={{
                      ...styles.bidRow,
                      backgroundColor:
                        idx === 0 ? "#006b4c" : "rgba(255,255,255,0.06)",
                      border:
                        idx === 0
                          ? "1.5px solid #0cbb1d"
                          : "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <div style={styles.bidPaleta}>
                      #{String(bid.palletNumber).padStart(3, "0")}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.bidAmount}>
                        {formatCOP(bid.amount)}
                      </div>
                      <div style={styles.bidDate}>
                        {formatDateEs(bid.createdAt)}
                      </div>
                    </div>
                    {idx === 0 && <div style={styles.checkIcon}>✓</div>}
                  </div>
                ))
              )}
            </div>

            {activeLot && !lotClosed && (
              <div style={styles.precioMinimo}>
                Precio mínimo:{" "}
                <strong>{formatCOP(activeLot.precioMinimo)}</strong>
              </div>
            )}

            {activeLot && (
              <div style={styles.lotCard}>
                <div style={styles.lotHeader}>
                  <span style={styles.lotBadge}>🔨 Subasta</span>
                  <span style={styles.lotNumber}>
                    Lote: <strong>{activeLot.nroLote}</strong>
                  </span>
                </div>
                <div style={styles.lotGrid}>
                  {[
                    ["Animales", activeLot.cantAnimales],
                    ["Tipo", activeLot.tipoGanado || "—"],
                    [
                      "Peso Prom",
                      activeLot.pesoProm ? `${activeLot.pesoProm}kg` : "—",
                    ],
                    [
                      "Peso Total",
                      activeLot.pesoTotal ? `${activeLot.pesoTotal}kg` : "—",
                    ],
                    ["Ubicación", activeLot.ubicacion || "—"],
                    [
                      "Valor Kg",
                      activeLot.valorKg ? formatCOP(activeLot.valorKg) : "—",
                    ],
                  ].map(([label, value]) => (
                    <div key={String(label)} style={styles.lotRow}>
                      <span style={styles.lotLabel}>{label}:</span>
                      <span style={styles.lotValue}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <span style={{ color: "#0cbb1d", fontWeight: 900, fontSize: 18 }}>
            Feria
          </span>
          <span style={{ color: "#fff", fontWeight: 900, fontSize: 18 }}>
            gana
          </span>
          <span
            style={{
              color: "rgba(255,255,255,0.4)",
              fontSize: 13,
              marginLeft: 16,
            }}
          >
            Subastas Ganaderas en Línea
          </span>
        </div>
      </div>
    </>
  );
}

// ─── ESTILOS (sin cambios respecto a versión Mux) ─────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  root: {
    width: "100vw",
    height: "100vh",
    backgroundColor: "#000",
    overflow: "hidden",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    position: "relative",
  },
  wsBadge: {
    position: "absolute",
    top: 12,
    right: 16,
    zIndex: 100,
    backgroundColor: "rgba(0,0,0,0.7)",
    borderRadius: 20,
    padding: "4px 14px",
    fontSize: 13,
    letterSpacing: 1,
  },
  grid: { position: "absolute", inset: 0 },
  colVideo: { position: "absolute", inset: 0 },
  colBids: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 380,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: "14px 16px",
    gap: 10,
    overflow: "hidden",
    background:
      "linear-gradient(to left, rgba(0,0,0,0.82) 60%, transparent 100%)",
  },
  videoWrapper: {
    position: "absolute",
    inset: 0,
    backgroundColor: "#000",
    overflow: "hidden",
  },
  videoPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0a1a2a",
  },
  videoEl: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    position: "absolute" as const,
    inset: 0,
  },
  liveBadge: {
    position: "absolute",
    top: 10,
    left: 10,
    zIndex: 10,
    color: "#fff",
    borderRadius: 4,
    padding: "3px 10px",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1,
  },
  lotCard: {
    backgroundColor: "#0f3644",
    borderRadius: 10,
    padding: "10px 15px",
    border: "1px solid rgba(255,255,255,0.08)",
    flexShrink: 0,
    paddingBottom: 40,
  },
  lotHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
    justifyContent: "center",
  },
  lotBadge: {
    backgroundColor: "#f98115",
    color: "#fff",
    borderRadius: 20,
    padding: "3px 12px",
    fontWeight: 700,
    fontSize: 15,
  },
  lotNumber: { color: "#fff", fontSize: 22, fontWeight: 800 },
  lotGrid: { display: "flex", flexDirection: "column", gap: 4 },
  lotRow: {
    display: "flex",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    padding: "4px 0",
  },
  lotLabel: { color: "rgba(255,255,255,0.6)", fontSize: 16 },
  lotValue: { color: "#fff", fontWeight: 700, fontSize: 15 },
  topBidCard: {
    backgroundColor: "#006b4c",
    borderRadius: 12,
    padding: "12px 16px",
    border: "1.5px solid #0cbb1d",
    textAlign: "center",
  },
  topBidLabel: {
    color: "rgba(255,255,255,0.8)",
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 2,
  },
  topBidPaleta: { color: "#fff", fontSize: 14, fontWeight: 700 },
  topBidAmount: {
    color: "#fff",
    fontSize: 32,
    fontWeight: 900,
    letterSpacing: -1,
  },
  topBidDate: { color: "rgba(255,255,255,0.6)", fontSize: 11 },
  bidsTitle: {
    color: "rgba(255,255,255,0.8)",
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  bidsList: {
    flex: 1,
    overflowY: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  noBids: {
    color: "rgba(255,255,255,0.3)",
    fontSize: 13,
    textAlign: "center",
    padding: "20px 0",
  },
  bidRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    borderRadius: 10,
  },
  bidPaleta: {
    color: "#f98115",
    fontWeight: 800,
    fontSize: 15,
    width: 52,
    flexShrink: 0,
  },
  bidAmount: { color: "#fff", fontWeight: 700, fontSize: 16 },
  bidDate: { color: "rgba(255,255,255,0.45)", fontSize: 10 },
  checkIcon: { color: "#0cbb1d", fontWeight: 900, fontSize: 16, flexShrink: 0 },
  precioMinimo: {
    color: "#B3B3B3",
    fontSize: 17,
    textAlign: "center",
    borderTop: "1px solid rgba(255,255,255,0.06)",
    paddingTop: 8,
  },
  winnerCard: {
    backgroundColor: "#0f3a1a",
    border: "2px solid #0cbb1d",
    borderRadius: 14,
    padding: "16px",
    textAlign: "center",
  },
  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 36,
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderTop: "1px solid rgba(255,255,255,0.07)",
  },
};
